import api from '../api/axios';
import { ChildProfile, DeletedChildProfile, ChildDeletionEvent } from '../models/childProfile';

export class ChildProfileDataService {
  /**
   * @param includeInactive  Admin-only. Backend silently ignores from non-admins.
   *   Default false so Posts/Schedule/Reports never see archived children.
   */
  static async getChildProfiles(includeInactive = false): Promise<ChildProfile[]> {
    const response = await api.get(`/child_profiles`, {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return response.data;
  }

  static async createChildProfile(child: ChildProfile): Promise<ChildProfile> {
    const response = await api.post(`/child_profiles`, child);
    // The API returns { message: string, id: number, unique_id: string }
    return { ...child, id: response.data.id, unique_id: response.data.unique_id };
  }

  static async updateChildProfile(child: ChildProfile): Promise<void> {
    await api.put(`/child_profiles/${child.id}`, child);
  }

  // ── Leaver lifecycle (child-deletion epic) — all Admin-only ─────────────

  /** Mark a child as left. Returns the computed hard-delete due date. */
  static async softDelete(childId: number, leftAt: string, reason?: string):
      Promise<{ leftAt: string; hardDeleteDueAt: string; anchorUsed: 'dob25' | 'leaving' }> {
    const response = await api.post(`/child_profiles/${childId}/soft-delete`, { leftAt, reason });
    return response.data;
  }

  /** Undo a soft delete. The child returns to the admin grid as inactive. */
  static async restore(childId: number): Promise<void> {
    await api.post(`/child_profiles/${childId}/restore`);
  }

  /** Leavers listing: soft-deleted children with retention status. */
  static async getDeleted(): Promise<DeletedChildProfile[]> {
    const response = await api.get(`/child_profiles/deleted`);
    return response.data;
  }

  /** How many leavers exist — drives whether Admin shows the Leavers tab at
   *  all. Separate from getDeleted so the common "none" case costs no
   *  decryption and ships no names. */
  static async getDeletedCount(): Promise<number> {
    const response = await api.get(`/child_profiles/deleted/count`);
    return response.data.count;
  }

  /** Override the hard-delete due date. Reason is mandatory (audited). */
  static async updateHardDeleteDueDate(childId: number, dueAt: string, reason: string): Promise<void> {
    await api.put(`/child_profiles/${childId}/hard-delete-due-date`, { dueAt, reason });
  }

  /** Proof-ledger history. Accepts a numeric id or (post-purge) a unique_id UUID. */
  static async getDeletionEvents(idOrUniqueId: number | string): Promise<ChildDeletionEvent[]> {
    const response = await api.get(`/child_profiles/${idOrUniqueId}/deletion-events`);
    return response.data;
  }

  /** Row counts of everything a hard delete will destroy (confirmation modal). */
  static async getPurgeInventory(childId: number): Promise<PurgeInventory> {
    const response = await api.get(`/child_profiles/${childId}/purge-inventory`);
    return response.data;
  }

  /**
   * Permanent deletion. Requires a fresh password re-auth (the API rejects
   * stale tokens with code REAUTH_REQUIRED). overrideEarly + reason are
   * required when the retention date has not been reached.
   */
  static async hardDelete(childId: number, opts?: { overrideEarly?: boolean; reason?: string }):
      Promise<{ childUniqueId: string; counts: Record<string, number>; s3ObjectsDeleted: number }> {
    const response = await api.post(`/child_profiles/${childId}/hard-delete`, opts ?? {});
    return response.data;
  }

  /** Finish a purge whose stored-file deletion failed (DB already purged). */
  static async retryHardDeleteS3(childUniqueId: string): Promise<{ s3ObjectsDeleted: number }> {
    const response = await api.post(`/child_profiles/hard-delete/retry-s3`, { childUniqueId });
    return response.data;
  }
}

// Shape of GET /child_profiles/:id/purge-inventory.
export interface PurgeInventory {
  posts: number;
  sharedPosts: number;
  uploads: number;
  documents: number;
  reports: number;
  ehcpTargets: number;
  attendanceLogs: number;
  scheduleItems: number;
  sentEmails: number;
  parentLinks: number;
}
